import { useState, useEffect, useRef } from 'react';
import { Tile, TileMessage } from './Tile';
import { TickerList } from './TickerList';
import { useIsMobile } from '../hooks/useIsMobile';
import { MSG_LIFETIME } from '../lib/energy';

const rand = (a: number, b: number) => a + Math.random() * (b - a);

interface Bot {
  id: string;
  name: string;
  color: string;
  charDelay: [number, number];
  pauseDelay: [number, number];
  startDelay: number;
  lines: string[];
}

const BOTS: Bot[] = [
  {
    id: 'maya', name: 'maya', color: '#ff6b2b',
    charDelay: [32, 88], pauseDelay: [2800, 5200], startDelay: 900,
    lines: [
      'hey everyone! just shared the link on twitter',
      'this is so different from group chat',
      'love how you can see the words forming',
      'feels like we are all in the same room',
      'anyone else find this kinda calming?',
      'the way messages dissolve is nice',
      'no scroll anxiety whatsoever',
    ],
  },
  {
    id: 'kai', name: 'kai', color: '#4ecdc4',
    charDelay: [48, 125], pauseDelay: [3200, 7200], startDelay: 3200,
    lines: [
      'whoa ok this is wild',
      'watching thoughts form in real time',
      'way more intimate than discord',
      'no pressure to keep up',
      'just vibes honestly',
      'the energy glow is a nice touch',
    ],
  },
  {
    id: 'luna', name: 'luna', color: '#ff85a1',
    charDelay: [28, 68], pauseDelay: [4200, 9500], startDelay: 7000,
    lines: [
      'just clicked the link from discord',
      'ooh the tiles!',
      'this is so cool',
      'i want this for everything',
      'ok sharing this everywhere',
    ],
  },
  {
    id: 'zeph', name: 'zeph', color: '#a78bfa',
    charDelay: [55, 145], pauseDelay: [7000, 18000], startDelay: 12000,
    lines: [
      'hey just found the link',
      'oh this is different',
      'i see the appeal now',
    ],
  },
  {
    id: 'reed', name: 'reed', color: '#34d399',
    charDelay: [40, 100], pauseDelay: [10000, 25000], startDelay: 20000,
    lines: [
      'lurking for now',
      'interesting concept',
    ],
  },
  {
    id: 'nova', name: 'nova', color: '#f472b6',
    charDelay: [35, 95], pauseDelay: [3500, 6000], startDelay: 2000,
    lines: [
      'omg who sent me this link',
      'wait i can see you all typing??',
      'this is the future',
      'never going back to slack',
    ],
  },
  {
    id: 'ash', name: 'ash', color: '#60a5fa',
    charDelay: [42, 110], pauseDelay: [5000, 12000], startDelay: 5500,
    lines: [
      'just got here',
      'the fade effect is mesmerizing',
      'feels very zen',
    ],
  },
  {
    id: 'sage', name: 'sage', color: '#fbbf24',
    charDelay: [30, 75], pauseDelay: [8000, 20000], startDelay: 15000,
    lines: [
      'hi from tokyo',
      'beautiful interface',
    ],
  },
  {
    id: 'rio', name: 'rio', color: '#fb923c',
    charDelay: [50, 130], pauseDelay: [6000, 14000], startDelay: 9000,
    lines: [
      'someone posted this in our group chat',
      'ok this beats whatsapp',
      'the presence thing is really cool',
    ],
  },
];

interface BotState {
  bot: Bot;
  buffer: string;
  messages: TileMessage[];
  lineIndex: number;
  charIndex: number;
  typing: boolean;
  lastActivity: number | null;
  nextAction: number;
}

export function DemoSpace() {
  const [now, setNow] = useState(Date.now);
  const isMobile = useIsMobile();
  const botsRef = useRef<BotState[]>(
    BOTS.map(bot => ({
      bot,
      buffer: '',
      messages: [],
      lineIndex: 0,
      charIndex: 0,
      typing: false,
      lastActivity: null,
      nextAction: Date.now() + bot.startDelay,
    }))
  );
  const [bots, setBots] = useState(botsRef.current);
  const [localBuffer, setLocalBuffer] = useState('');
  const [localLastActivity, setLocalLastActivity] = useState<number | null>(null);
  const [localMessages, setLocalMessages] = useState<TileMessage[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const tick = () => {
      const t = Date.now();
      setNow(t);

      let changed = false;
      const states = botsRef.current;

      for (let i = 0; i < states.length; i++) {
        const s = states[i];
        if (t < s.nextAction) continue;

        changed = true;

        if (!s.typing) {
          if (s.lineIndex >= s.bot.lines.length) {
            s.lineIndex = 0;
          }
          s.typing = true;
          s.charIndex = 0;
          s.buffer = '';
          s.lastActivity = t;
          s.nextAction = t + rand(s.bot.charDelay[0], s.bot.charDelay[1]);
        } else {
          const line = s.bot.lines[s.lineIndex];
          if (s.charIndex < line.length) {
            s.charIndex++;
            s.buffer = line.slice(0, s.charIndex);
            s.lastActivity = t;
            const ch = line[s.charIndex - 1];
            const delay = ch === ' '
              ? rand(s.bot.charDelay[0] * 1.6, s.bot.charDelay[1] * 2.2)
              : Math.random() < 0.07
                ? rand(180, 480)
                : rand(s.bot.charDelay[0], s.bot.charDelay[1]);
            s.nextAction = t + delay;
          } else {
            s.messages = [
              ...s.messages.filter(m => t - m.submittedAt < MSG_LIFETIME),
              { id: `${s.bot.id}-${s.lineIndex}-${t}`, text: s.buffer, submittedAt: t },
            ];
            s.buffer = '';
            s.typing = false;
            s.lineIndex++;
            s.lastActivity = t;
            s.nextAction = t + rand(s.bot.pauseDelay[0], s.bot.pauseDelay[1]);
          }
        }
      }

      if (changed) {
        setBots([...states]);
      }

      rafId = requestAnimationFrame(tick);
    };

    let rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const visibleLocal = localMessages.filter(m => now - m.submittedAt < MSG_LIFETIME);
  if (visibleLocal.length !== localMessages.length) {
    setTimeout(() => setLocalMessages(visibleLocal), 0);
  }

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalBuffer(e.target.value);
    setLocalLastActivity(Date.now());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newBuffer = localBuffer + '↵';
    setLocalBuffer(newBuffer);
    setLocalLastActivity(Date.now());
  };

  const userCount = bots.length + 1;

  return (
    <div
      className="pulse-screen"
      style={{
        background: 'linear-gradient(155deg, #07070d 0%, #0b0b16 35%, #080812 100%)',
        fontFamily: '"DM Sans", sans-serif',
        color: '#e8e4dc',
        display: 'flex',
        flexDirection: 'column',
        padding: '10px 12px 12px',
        position: 'relative',
        overflow: 'hidden',
      }}
      onClick={() => inputRef.current?.focus()}
    >
      {/* Ambient orbs */}
      <div style={{ position: 'fixed', top: '12%', left: '3%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(255,107,43,.022) 0%, transparent 60%)', pointerEvents: 'none', animation: 'drift 25s ease-in-out infinite' }} />
      <div style={{ position: 'fixed', bottom: '8%', right: '3%', width: 450, height: 450, background: 'radial-gradient(circle, rgba(78,205,196,.018) 0%, transparent 60%)', pointerEvents: 'none', animation: 'drift2 30s ease-in-out infinite' }} />

      {/* Hidden input */}
      {/* Header */}
      <header style={{
        display: 'flex', alignItems: 'center',
        padding: '6px 2px 8px', marginBottom: 6,
        borderBottom: '1px solid rgba(255,255,255,.04)',
        flexShrink: 0, gap: 10,
      }}>
        <span style={{ color: '#ff6b2b', fontSize: 14, animation: 'breathe 4s ease-in-out infinite', lineHeight: 1 }}>●</span>
        <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 13, fontWeight: 700, letterSpacing: 2.5, color: '#e8e4dc' }}>pulse</span>
        <span style={{ color: '#222', fontSize: 12 }}>·</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#4a4a4a', fontFamily: '"DM Sans", sans-serif' }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#4ecdc4', display: 'inline-block', animation: 'breathe 2.5s ease-in-out infinite' }} />
          {userCount} here
        </div>
      </header>

      {isMobile ? (
        <TickerList
          users={bots.map(s => ({
            id: s.bot.id,
            name: s.bot.name,
            color: s.bot.color,
            buffer: s.buffer,
            messages: s.messages.filter(m => now - m.submittedAt < MSG_LIFETIME),
            lastActivity: s.lastActivity,
          }))}
          now={now}
        />
      ) : (
        <div className="pulse-grid">
          <Tile
            name="you"
            color="#fbbf24"
            buffer={localBuffer}
            messages={visibleLocal}
            lastActivity={localLastActivity}
            isUser={true}
            now={now}
          />
          {bots.map(s => (
            <Tile
              key={s.bot.id}
              name={s.bot.name}
              color={s.bot.color}
              buffer={s.buffer}
              messages={s.messages.filter(m => now - m.submittedAt < MSG_LIFETIME)}
              lastActivity={s.lastActivity}
              isUser={false}
              now={now}
            />
          ))}
        </div>
      )}

      {/* Chat input pinned to bottom */}
      <form
        onSubmit={handleSubmit}
        className="pulse-chat-input"
      >
        <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#fbbf24', flexShrink: 0 }} />
        <input
          ref={inputRef}
          value={localBuffer}
          onChange={handleInput}
          placeholder="type something..."
          autoFocus
          style={{
            flex: 1, background: 'none', border: 'none', outline: 'none',
            color: '#e8e4dc', fontFamily: '"DM Sans", sans-serif', fontSize: 14,
            caretColor: '#fbbf24',
          }}
        />
        <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 10, color: '#333', letterSpacing: 1, flexShrink: 0 }}>
          ↵ live
        </span>
      </form>
    </div>
  );
}
